import { ArpFields } from "constants/forecasts.constants";
import { parseISO } from "date-fns";

import { ArpSegment } from "models/UserArpsModel";

import useDefaultTypeWell from "../hooks/useDefaultTypeWell";
import { DEFAULT_TFLAT } from "./arpsConstants";
import {
  getDiAtDayFromQiToQf,
  getTypeWellTemplateDisplayFieldsAndData,
  isFlatSegment
} from "./arpsUtils";
import { addAvgMonthsToDate, avgMonthsDiff, getDurationInDays } from "./dates";
import { updateRampUpSegmentValues } from "./updateSegment";
import { arpsWasm } from "./wasmLoader";

export interface ExtendSegment {
  (params: { productArps: ArpSegment[]; wellKey: string | undefined }): ArpSegment[];
}

const useExtendSegments = () => {
  const { getDefaultTypeWell } = useDefaultTypeWell();

  const calculateQf = (di: number, df: number, qi: number, b: number): number => {
    const originalQf = arpsWasm.getRateAtNominal(di, df, qi, b);
    return Math.round((originalQf + Number.EPSILON) * 1000000) / 1000000;
  };

  const updateSegmentDfFromSessionStorageOrUseDefault = ({
    segment,
    product,
    wellKey
  }: {
    segment: ArpSegment;
    product: string;
    wellKey: string | undefined;
  }): void => {
    const sessionDf = wellKey
      ? sessionStorage.getItem(`well_df_${wellKey}_${product}`)
      : null;
    const defaultTypeWell = getDefaultTypeWell(product);
    segment.df = sessionDf !== null ? parseFloat(sessionDf) : defaultTypeWell.values.Df;
  };

  const extendOneSegmentTerminalDecline: ExtendSegment = ({ productArps }) => {
    const product = productArps[0].product;
    const defaultTypeWell = getDefaultTypeWell(product);

    const currentValues = getTypeWellTemplateDisplayFieldsAndData(
      null,
      productArps,
      null,
      defaultTypeWell
    );

    const bVal = currentValues.Bhyp ?? defaultTypeWell.values.Bhyp;

    const seg1 = {
      product,
      q0: currentValues.Q0,
      qi: currentValues.Qi,
      b: bVal,
      qf: parseFloat(currentValues.Qf.toString()),
      df: currentValues.Df,
      di: currentValues.Di,
      startDate: currentValues["Start Date"],
      segmentIndex: 1,
      trendCum: 0,
      endDate: currentValues["Start Date"],
      maxRate: currentValues.maxRate
    };

    return [seg1];
  };

  const extendTwoSegmentTerminalDecline: ExtendSegment = ({ productArps, wellKey }) => {
    const product = productArps[0].product;
    const defaultTypeWell = getDefaultTypeWell(product);

    const currentValues = getTypeWellTemplateDisplayFieldsAndData(
      null,
      productArps,
      null,
      defaultTypeWell
    );

    const seg1: ArpSegment = {
      product,
      q0: currentValues.Q0,
      qi: currentValues.Qi,
      b: currentValues.Bhyp ?? defaultTypeWell.values.Bhyp,
      qf: parseFloat(currentValues.Qf.toString()),
      df: currentValues.Df,
      di: currentValues.Di,
      startDate: currentValues["Start Date"],
      segmentIndex: 1,
      trendCum: 0,
      endDate: currentValues["Start Date"],
      maxRate: currentValues.maxRate
    };

    updateSegmentDfFromSessionStorageOrUseDefault({
      wellKey,
      segment: seg1,
      product
    });
    seg1.qf = arpsWasm.getRateAtNominal(seg1.di, seg1.df, seg1.qi, seg1.b);

    const seg2: ArpSegment = {
      product,
      qi: seg1.qf,
      b: currentValues.Bf ?? defaultTypeWell?.values?.Bf,
      qf: parseFloat(currentValues.Qf.toString()),
      df: seg1.df,
      di: seg1.df,
      startDate: seg1.endDate,
      segmentIndex: 2,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    } as ArpSegment;

    return [seg1].concat([seg2]);
  };

  const extendTwoSegmentTerminalDeclineWithRampUp: ExtendSegment = ({
    productArps,
    wellKey
  }) => {
    const product = productArps[0].product;
    const defaultTypeWell = getDefaultTypeWell(product);

    const currentValues = getTypeWellTemplateDisplayFieldsAndData(
      null,
      productArps,
      null,
      defaultTypeWell
    );

    const startDate = new Date(currentValues["Start Date"]);
    let endDate = new Date(currentValues["Start Date"]);
    const monthsToAdd = currentValues.tRamp ?? defaultTypeWell.values.tramp;
    endDate = addAvgMonthsToDate(endDate, monthsToAdd).toDate();
    const seg1: ArpSegment = {
      product,
      // When switching from non-ramp-up to ramp-up, use default Q0 if no existing q0 field
      // When already in ramp-up mode, preserve existing Q0
      qi: productArps[0].q0 !== undefined ? productArps[0].q0 : defaultTypeWell.values.Q0,
      b: 0,
      qf: currentValues.Qi,
      df: -1, //TODO: calculate the correct df based on Q0 and Qi
      di: -1, //TODO: calculate the correct di based on Q0 and Qi
      startDate: startDate.toISOString(),
      segmentIndex: 1,
      trendCum: 0,
      endDate: endDate.toISOString(),
      maxRate: currentValues.maxRate
    };
    // Set q0 to the same value as qi for ramp-up segments
    seg1.q0 = seg1.qi;
    updateRampUpSegmentValues(seg1, true);

    const seg2 = {
      product,
      qi: seg1.qf,
      b: currentValues.Bhyp ?? defaultTypeWell.values.Bhyp,
      qf: seg1.qf,
      df: currentValues.Df,
      di: currentValues.Di,
      startDate: seg1.endDate,
      segmentIndex: 2,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    };

    updateSegmentDfFromSessionStorageOrUseDefault({
      segment: seg2,
      product,
      wellKey
    });
    seg2.qf = calculateQf(seg2.di, seg2.df, seg2.qi, seg2.b);

    const seg3 = {
      product,
      qi: seg2.qf,
      b: currentValues.Bf ?? defaultTypeWell.values.Bf,
      qf: parseFloat(currentValues.Qf.toString()),
      df: seg2.df,
      di: seg2.df,
      startDate: seg2.endDate,
      segmentIndex: 3,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    } as ArpSegment;

    return [seg1].concat([seg2, seg3]);
  };

  const extendThreeSegmentTerminalDecline: ExtendSegment = ({ productArps, wellKey }) => {
    const product = productArps[0].product;
    const defaultTypeWell = getDefaultTypeWell(product);

    const currentValues = getTypeWellTemplateDisplayFieldsAndData(
      null,
      productArps,
      null,
      defaultTypeWell
    );

    const bTrans = currentValues.Btrans ?? defaultTypeWell.values.Btrans;
    const tTrans = currentValues.tTrans ?? defaultTypeWell.values.ttrans;

    const startDate = parseISO(currentValues["Start Date"]);
    const tTransEndDate = addAvgMonthsToDate(startDate, tTrans);
    const daysInBetween = getDurationInDays(
      startDate.toISOString(),
      tTransEndDate.toISOString()
    );

    const seg1: ArpSegment = {
      product,
      q0: currentValues.Q0,
      qi: currentValues.Qi,
      b: bTrans,
      qf: arpsWasm.getRateAtDay(
        currentValues.Qi,
        currentValues.Di,
        bTrans,
        daysInBetween
      ),
      df: currentValues.Di,
      di: currentValues.Di,
      startDate: currentValues["Start Date"],
      segmentIndex: 1,
      trendCum: 0,
      endDate: currentValues["Start Date"],
      maxRate: currentValues.maxRate
    };

    let endDate = new Date(seg1.startDate);
    endDate = addAvgMonthsToDate(endDate, tTrans).toDate();
    seg1.endDate = endDate.toISOString();

    const seg2 = {
      product,
      qi: seg1.qf,
      b: currentValues.Bhyp ?? defaultTypeWell.values.Bhyp,
      qf: 100.0,
      df: currentValues.Df,
      di: seg1.df,
      startDate: seg1.endDate,
      segmentIndex: 2,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    };

    updateSegmentDfFromSessionStorageOrUseDefault({
      segment: seg2,
      product,
      wellKey
    });

    seg2.qf = calculateQf(seg2.di, seg2.df, seg2.qi, seg2.b);

    const seg3 = {
      product,
      qi: seg2.qf,
      b: currentValues.Bf ?? defaultTypeWell.values.Bf,
      qf: currentValues.Qf,
      df: seg2.df,
      di: seg2.df,
      startDate: seg2.endDate,
      segmentIndex: 3,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    } as ArpSegment;
    return [seg1].concat([seg2, seg3]);
  };

  const extendThreeSegmentTerminalDeclineWithRampUp: ExtendSegment = ({
    productArps,
    wellKey
  }) => {
    const product = productArps[0].product;
    const defaultTypeWell = getDefaultTypeWell(product);

    const currentValues = getTypeWellTemplateDisplayFieldsAndData(
      null,
      productArps,
      null,
      defaultTypeWell
    );

    const startDate = new Date(currentValues["Start Date"]);
    const monthsToAdd = currentValues.tRamp ?? defaultTypeWell.values.tramp;
    const endDate = addAvgMonthsToDate(startDate, monthsToAdd);
    const seg1: ArpSegment = {
      product,
      // Preserve user-entered Q0 values, fallback to default only if Q0 is not available
      qi: currentValues.Q0 ?? defaultTypeWell.values.Q0,
      b: 0,
      qf: currentValues.Qi,
      df: -1, //TODO: calculate the correct df based on Q0 and Qi
      di: -1, //TODO: calculate the correct di based on Q0 and Qi
      startDate: startDate.toISOString(),
      segmentIndex: 1,
      trendCum: 0,
      endDate: endDate.toISOString(),
      maxRate: currentValues.maxRate
    };
    // Preserve Q0 value if it exists
    if (currentValues.Q0 !== undefined) {
      seg1.q0 = currentValues.Q0;
    }

    updateRampUpSegmentValues(seg1, true);
    const bTrans = currentValues.Btrans ?? defaultTypeWell.values.Btrans;
    const tTrans = currentValues.tTrans ?? defaultTypeWell.values.ttrans;

    const tTransEndDate = addAvgMonthsToDate(parseISO(seg1.startDate), tTrans);
    const daysInBetween = getDurationInDays(
      startDate.toISOString(),
      tTransEndDate.toISOString()
    );

    const seg2 = {
      product,
      qi: seg1.qf,
      b: bTrans,
      qf: arpsWasm.getRateAtDay(
        currentValues.Qi,
        currentValues.Di,
        bTrans,
        daysInBetween
      ),
      df: currentValues.Df,
      di: currentValues.Di,
      startDate: seg1.endDate,
      segmentIndex: 2,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    };

    let s2endDate = new Date(seg2.startDate);
    s2endDate = addAvgMonthsToDate(s2endDate, tTrans).toDate();
    seg2.endDate = s2endDate.toISOString();

    const seg3 = {
      product,
      qi: seg2.qf,
      b: currentValues.Bhyp ?? defaultTypeWell.values.Bhyp,
      qf: 100.0,
      df: currentValues.Df,
      di: seg2.df,
      startDate: seg2.endDate,
      segmentIndex: 3,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    };

    updateSegmentDfFromSessionStorageOrUseDefault({
      segment: seg3,
      product,
      wellKey
    });
    seg3.qf = calculateQf(seg3.di, seg3.df, seg3.qi, seg3.b);

    const seg4 = {
      product,
      qi: seg3.qf,
      b: currentValues.Bf ?? defaultTypeWell.values.Bf,
      qf: currentValues.Qf,
      df: seg3.df,
      di: seg3.df,
      startDate: seg3.endDate,
      segmentIndex: 4,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    } as ArpSegment;

    return [seg1].concat([seg2, seg3, seg4]);
  };

  const extendThreeSegmentTerminalDeclineWithConstrainedPeriod: ExtendSegment = ({
    productArps,
    wellKey
  }) => {
    const product = productArps[0].product;
    const defaultTypeWell = getDefaultTypeWell(product);

    const currentValues = getTypeWellTemplateDisplayFieldsAndData(
      null,
      productArps,
      null,
      defaultTypeWell
    );

    const startDate = new Date(currentValues["Start Date"]);
    const tRamp = currentValues.tRamp ?? defaultTypeWell.values.tramp;
    const endDate = addAvgMonthsToDate(startDate, tRamp);

    // Determine Q0: preserve existing q0 if available, otherwise use default
    const q0Value =
      productArps[0].q0 !== undefined ? productArps[0].q0 : defaultTypeWell.values.Q0;
    const constrainedQi = q0Value * 1.1;

    const seg1: ArpSegment = {
      product,
      q0: q0Value,
      qi: constrainedQi,
      b: currentValues.Btrans,
      qf: currentValues.Qi, // This should remain as the target rate from the main decline segment
      di: currentValues.Di,
      df: currentValues.Di,
      startDate: startDate.toISOString(),
      segmentIndex: 1,
      trendCum: 0,
      endDate: endDate.toISOString(),
      maxRate: currentValues.maxRate
    };

    const tTransEndDate = addAvgMonthsToDate(parseISO(seg1.startDate), tRamp);
    const daysInBetween = getDurationInDays(
      startDate.toISOString(),
      tTransEndDate.toISOString()
    );

    if (seg1.qi > seg1.qf) {
      seg1.di = getDiAtDayFromQiToQf(seg1.qi, seg1.qf, seg1.b, daysInBetween);
    }

    const bTrans = currentValues.Btrans ?? defaultTypeWell.values.Btrans;
    const tTrans = currentValues.tTrans ?? defaultTypeWell.values.ttrans;

    const seg2: ArpSegment = {
      product,
      qi: seg1.qf,
      b: bTrans,
      qf: arpsWasm.getRateAtDay(
        currentValues.Qi,
        currentValues.Di,
        bTrans,
        daysInBetween
      ),
      df: currentValues.Df,
      di: currentValues.Di,
      startDate: seg1.endDate,
      segmentIndex: 2,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    };

    let s2endDate = new Date(seg2.startDate);
    s2endDate = addAvgMonthsToDate(s2endDate, tTrans).toDate();
    seg2.endDate = s2endDate.toISOString();

    const seg3: ArpSegment = {
      product,
      qi: seg2.qf,
      b: currentValues.Bhyp ?? defaultTypeWell.values.Bhyp,
      qf: 100.0,
      df: currentValues.Df,
      di: seg2.df,
      startDate: seg2.endDate,
      segmentIndex: 3,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    };

    updateSegmentDfFromSessionStorageOrUseDefault({
      segment: seg3,
      product,
      wellKey
    });

    seg3.qf = calculateQf(seg3.di, seg3.df, seg3.qi, seg3.b);

    const seg4: ArpSegment = {
      product,
      qi: seg3.qf,
      b: currentValues.Bf ?? defaultTypeWell.values.Bf,
      qf: Number(currentValues.Qf),
      df: seg3.df,
      di: seg3.df,
      startDate: seg3.endDate,
      segmentIndex: 4,
      trendCum: 0,
      endDate: new Date().toISOString(),
      maxRate: currentValues.maxRate
    };

    return [seg1].concat([seg2, seg3, seg4]);
  };

  /**
   * SAGD template: Extends existing segments to SAGD structure
   * Handles conversion from other templates and preserves existing values
   * Detects if ramp segment should be included based on tRamp value
   *
   * Note: wellKey is unused because SAGD uses exponential decline (b=0) where Df
   * is calculated mathematically from curve parameters rather than retrieved from
   * session storage like other templates.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const extendSAGD: ExtendSegment = ({ productArps, wellKey }) => {
    const product = productArps[0].product;
    const defaultTypeWell = getDefaultTypeWell(product);

    const currentValues = getTypeWellTemplateDisplayFieldsAndData(
      null,
      productArps,
      null,
      defaultTypeWell
    );

    // Determine if we have a ramp-up segment
    const hasRampUp = productArps.length >= 1 && productArps[0].qi < productArps[0].qf;

    // Find flat segment duration if it exists
    let tFlat = DEFAULT_TFLAT;
    const existingFlatSegment = productArps.find((seg) => isFlatSegment(seg));
    if (existingFlatSegment) {
      tFlat = Math.round(
        avgMonthsDiff(existingFlatSegment.startDate, existingFlatSegment.endDate)
      );
    }
    // Ensure tFlat is valid
    if (!tFlat || tFlat <= 0 || !isFinite(tFlat)) {
      tFlat = DEFAULT_TFLAT;
    }

    const startDate = currentValues[ArpFields.StartDate];
    const Qi = currentValues.Qi || defaultTypeWell.values.Qi;
    const Qf = Number(currentValues.Qf) || defaultTypeWell.values.Qf;

    // Get Di from a declining segment or use default
    const declineSegment = productArps.find((seg) => seg.di > 0 && seg.b >= 0);
    const Di = declineSegment?.di ?? currentValues.Di ?? defaultTypeWell.values.Di;

    // Calculate Df based on Di for exponential decline (b=0)
    const Df = arpsWasm.getNominalDeclineAtRate(Di, Qi, 0, Qf);

    if (hasRampUp && currentValues.tRamp && currentValues.tRamp > 0) {
      // 3-segment SAGD: ramp-up + flat + decline
      const rampEndDate = addAvgMonthsToDate(
        new Date(startDate),
        currentValues.tRamp
      ).toISOString();

      // Segment 1: Harmonic Ramp-up (b=-1) for SAGD
      const seg1: ArpSegment = {
        product,
        q0: currentValues.Q0 ?? defaultTypeWell.values.Q0,
        qi: currentValues.Q0 ?? defaultTypeWell.values.Q0,
        b: -1.0, // Harmonic ramp
        qf: Qi,
        df: 0,
        di: 0, // Will be calculated by updateRampUpSegmentValues
        startDate: startDate,
        segmentIndex: 1,
        trendCum: 0,
        endDate: rampEndDate,
        maxRate: currentValues.maxRate
      };
      seg1.q0 = seg1.qi;
      // Use harmonic ramp calculation (b=-1)
      updateRampUpSegmentValues(seg1, true);

      // Segment 2: Flat
      const flatEndDate = addAvgMonthsToDate(new Date(seg1.endDate), tFlat).toISOString();
      const seg2: ArpSegment = {
        product,
        qi: Qi,
        maxRate: currentValues.maxRate,
        b: 0,
        qf: Qi, // Flat: qi = qf
        df: 0,
        di: 0,
        startDate: seg1.endDate,
        segmentIndex: 2,
        trendCum: 0,
        endDate: flatEndDate
      };

      // Segment 3: Exponential decline (b=0)
      const seg3: ArpSegment = {
        product,
        qi: Qi,
        maxRate: currentValues.maxRate,
        b: 0, // Exponential decline
        qf: Qf,
        df: Df,
        di: Di, // Use the found/calculated Di
        startDate: seg2.endDate,
        segmentIndex: 3,
        trendCum: 0,
        endDate: new Date().toISOString() // Will be calculated by updateEndDates
      };

      return [seg1, seg2, seg3];
    } else {
      // 2-segment SAGD: flat + decline
      const flatEndDate = addAvgMonthsToDate(new Date(startDate), tFlat).toISOString();

      // Segment 1: Flat
      const seg1: ArpSegment = {
        product,
        qi: Qi,
        maxRate: currentValues.maxRate,
        b: 0,
        qf: Qi, // Flat: qi = qf
        df: 0,
        di: 0,
        startDate: startDate,
        segmentIndex: 1,
        trendCum: 0,
        endDate: flatEndDate
      };

      // Segment 2: Exponential decline (b=0)
      const seg2: ArpSegment = {
        product,
        qi: Qi,
        maxRate: currentValues.maxRate,
        b: 0, // Exponential decline
        qf: Qf,
        df: Df,
        di: Di, // Use the found/calculated Di
        startDate: seg1.endDate,
        segmentIndex: 2,
        trendCum: 0,
        endDate: new Date().toISOString() // Will be calculated by updateEndDates
      };

      return [seg1, seg2];
    }
  };

  return {
    extendOneSegmentTerminalDecline,
    extendTwoSegmentTerminalDecline,
    extendTwoSegmentTerminalDeclineWithRampUp,
    extendThreeSegmentTerminalDecline,
    extendThreeSegmentTerminalDeclineWithRampUp,
    extendThreeSegmentTerminalDeclineWithConstrainedPeriod,
    extendSAGD
  };
};

export default useExtendSegments;
